package io.horion.cv.pages.cv

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.fa.FaMoon
import com.varabyte.kobweb.silk.components.icons.fa.FaSun
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.viewModel.CvViewModel
import io.horion.cv.di.koinGet
import io.horion.cv.styles.DownloadButtonStyle
import io.horion.cv.styles.ModifyButtonStyle
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import org.jetbrains.compose.web.css.px
import io.horion.cv.api.utils.TokenRepository
import io.horion.cv.components.AuthRequiredPopup
import io.horion.cv.components.LoadingPopup
import io.horion.cv.components.focusMissingFieldFromPreview
import io.horion.cv.components.preview.CvPreviewPanel
import io.horion.cv.styles.CvPreviewActionIconStyle
import io.horion.cv.styles.CvPreviewActionsStyle
import io.horion.cv.styles.CvPreviewBackButtonStyle
import io.horion.cv.styles.CvPreviewDownloadButtonStyle
import io.horion.cv.styles.CvPreviewHeaderIconStyle
import io.horion.cv.styles.CvPreviewMainContentStyle
import io.horion.cv.styles.CvPreviewModifyButtonStyle
import io.horion.cv.styles.CvPreviewPageStyle
import io.horion.cv.styles.CvPreviewThemeGroupStyle
import io.horion.cv.styles.CvPreviewThemeIconStyle
import io.horion.cv.styles.CvPreviewTitleStyle
import io.horion.cv.styles.CvPreviewTopbarStyle
import kotlinx.browser.window
import kotlinx.coroutines.launch

@Page("/cv_preview")
@Composable
fun CvPreviewPage() {
    val cvViewModel = koinGet<CvViewModel>()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    var showAuthModal by remember {
        mutableStateOf(false)
    }

    var isAuthenticated by remember {
        mutableStateOf(false)
    }

    if (showAuthModal) {
        AuthRequiredPopup(
            onDismiss = {
                showAuthModal = false
            },
            onConfirm = {
                showAuthModal = false
                TokenRepository.redirectToAuth()
            }
        )
    }

    LaunchedEffect(
        cvViewModel.cvUiState.cvCreateSuccess
    ) {
        if (cvViewModel.cvUiState.cvCreateSuccess) {
            ctx.router.navigateTo("/payment")
        }
    }

    LoadingPopup(
        cvViewModel.cvUiState.loading
    )

    val data = cvViewModel.cvUiState.data
    val model = cvViewModel.cvUiState.model

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    val pageBg = if (isDark) {
        Colors.DarkBG
    } else {
        Colors.White
    }

    Column(
        modifier = CvPreviewPageStyle
            .toModifier()
            .backgroundColor(pageBg)
            .styleModifier {
                property(
                    "transition",
                    "background-color 300ms ease"
                )
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        CvPreviewTopbar(
            isDark = isDark,
        )

        Column(
            modifier = CvPreviewMainContentStyle
                .toModifier()
                .padding(leftRight = 5.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            CvPreviewPanel(
                data = data,
                model = model,
                cvViewModel = cvViewModel,
                showZoomBar = false,
                initialZoom = 0.5
            )

            Row(
                modifier = CvPreviewActionsStyle.toModifier()
            ) {
                ActionButton(
                    text = "Modifier",
                    iconSrc = "/edit.svg",
                    buttonModifier = ModifyButtonStyle
                        .toModifier()
                        .then(
                            CvPreviewModifyButtonStyle.toModifier()
                        )
                        .fontFamily(INTER)
                        .color(Colors.White),
                    iconModifier = CvPreviewActionIconStyle.toModifier(),
                    onClick = {
                        ctx.router.navigateTo("/info_pers")
                    }
                )

                ActionButton(
                    text = "Télécharger",
                    iconSrc = "/download.svg",
                    buttonModifier = DownloadButtonStyle
                        .toModifier()
                        .then(
                            CvPreviewDownloadButtonStyle.toModifier()
                        )
                        .fontFamily(INTER)
                        .color(Colors.White),
                    iconModifier = CvPreviewActionIconStyle.toModifier(),
                    onClick = {
                        if (cvViewModel.validateRequiredFields()) {
                            scope.launch {
                                isAuthenticated =
                                    runCatching {
                                        TokenRepository.checkAuthentication()
                                    }.getOrDefault(false)

                                if (isAuthenticated) {
                                    cvViewModel.createCv()
                                } else {
                                    showAuthModal = true
                                }
                            }
                        } else {
                            cvViewModel.missingFieldIds
                                .firstOrNull()
                                ?.let { fieldId ->
                                    focusMissingFieldFromPreview(fieldId)
                                }
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun CvPreviewTopbar(
    isDark: Boolean,
) {
    var colorMode by ColorMode.currentState
    val textColor = if (isDark) {
        Colors.White
    } else {
        Colors.Primary
    }

    val iconFilter = if (isDark) {
        "none"
    } else {
        "invert(1)"
    }

    Row(
        modifier = CvPreviewTopbarStyle
            .toModifier()
            .backgroundColor(
                if (isDark) Colors.DarkBG else Colors.White
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Link(
            path = "/info_pers",
            modifier = Modifier
                .textDecorationLine(TextDecorationLine.None)
                .onClick {
                    window.history.back()
                }
        ) {
            Row(
                modifier = CvPreviewBackButtonStyle
                    .toModifier()
                    .cursor(Cursor.Pointer),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    src = "/arrow-left-circle.svg",
                    modifier = CvPreviewHeaderIconStyle
                        .toModifier()
                        .styleModifier {
                            property("filter", iconFilter)
                        }
                )

                SpanText(
                    text = "Modifier",
                    modifier = Modifier
                        .margin(left = 10.px)
                        .fontFamily(INTER)
                        .color(textColor)
                )
            }
        }

        Spacer()

        SpanText(
            text = "Cv sans titre",
            modifier = CvPreviewTitleStyle
                .toModifier()
                .fontFamily(INTER)
                .fontWeight(FontWeight.Bold)
                .color(textColor)
        )

        Image(
            src = "/cloud-check.svg",
            modifier = CvPreviewHeaderIconStyle
                .toModifier()
                .styleModifier {
                    property("filter", iconFilter)
                }
        )

        Spacer()

        Row(
            modifier = CvPreviewThemeGroupStyle.toModifier(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (isDark) {
                FaSun(
                    modifier = CvPreviewThemeIconStyle
                        .toModifier()
                        .color(Colors.yellow)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            colorMode = colorMode.opposite
                        }
                )
            } else {
                FaMoon(
                    modifier = CvPreviewThemeIconStyle
                        .toModifier()
                        .color(Colors.Primary)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            colorMode = colorMode.opposite
                        }
                )
            }
        }
    }
}

@Composable
fun ActionButton(
    text: String,
    iconSrc: String,
    onClick: () -> Unit,
    buttonModifier: Modifier = Modifier,
    iconModifier: Modifier = Modifier,
) {
    Box(
        modifier = Modifier
            .borderRadius(7.px)
            .cursor(Cursor.Pointer)
            .then(buttonModifier)
            .onClick {
                onClick()
            },
        contentAlignment = Alignment.Center
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically
        ) {
            Image(
                src = iconSrc,
                description = "$text icon",
                modifier = iconModifier
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
            )

            SpanText(
                text = text,
                modifier = Modifier
                    .margin(left = 10.px)
                    .fontFamily(INTER)
            )
        }
    }
}